import '.a{display:flex;}.ssr-plugin'
import '.bk{gap:8px;}.ssr-plugin'
import '.a7{position:relative;}.ssr-plugin'
import { useState, useEffect } from 'react'
import { styled } from 'inlines'
import { COLOR_FORMAT } from './utils.js'
import { colors } from '../../tokens/colors.js'
import { SelectInput } from '../SelectInput/index.js'
import { TextInput } from '../TextInput/index.js'
import { IconButton } from '../IconButton/index.js'
import { isHex, isValidRGBA, isValidRGB, isValidHSLA } from './utils.js'
import { Stack } from '../Stack/index.js'

type ColorSettingsProps = {
  colorFormat: COLOR_FORMAT
  setColorFormat: (colorFormat: COLOR_FORMAT) => void
  colorValue: string
  onChange: (color: string) => void
}

export const ColorSettings = ({
  colorFormat,
  setColorFormat,
  colorValue,
  onChange,
}: ColorSettingsProps) => {
  const [inputTextValue, setInputTextValue] = useState('')
  const [focused, setFocused] = useState(false)

  // Function to format the color value for display in the input
  const formatColorForInput = (color: string, format: COLOR_FORMAT) => {
    if (format === 'HEX') {
      return color.startsWith('#') ? color.slice(1) : color
    } else if (format === 'RGB' || format === 'HSL') {
      const match = color.match(/\(([^)]*)\)/)
      return match ? match[1] : color
    }
    return color
  }

  // Update inputTextValue when colorValue or colorFormat changes from parent
  useEffect(() => {
    if (!focused) {
      setInputTextValue(formatColorForInput(colorValue, colorFormat))
    }
  }, [colorValue, colorFormat, focused])

  const handleColorChange = (value: string) => {
    let newColor = value

    if (colorFormat === 'HEX') {
      if (isHex(value)) {
        newColor = value
      } else if (isHex('#' + value)) {
        newColor = '#' + value
      } else {
        newColor = '#000000'
      }
    } else if (colorFormat === 'RGB') {
      if (isValidRGBA('rgba(' + value + ')')) {
        newColor = 'rgba(' + value + ')'
      } else if (isValidRGB(value)) {
        newColor = 'rgba(' + value.substring(4).slice(0, -1) + ', 1)'
      } else if (isValidRGBA(value)) {
        newColor = value
      } else {
        newColor = 'rgba(0,0,0,1)'
      }
    } else if (colorFormat === 'HSL') {
      if (isValidHSLA('hsla(' + value + ')')) {
        newColor = 'hsla(' + value + ')'
      } else if (isValidHSLA(value)) {
        newColor = value
      } else {
        newColor = 'hsla(0,0%,0%,1)'
      }
    }
    onChange(newColor)
  }

  const handleBlur = () => {
    setFocused(false)
    if (
      inputTextValue === '' ||
      (!isHex(inputTextValue) &&
        !isValidRGBA(inputTextValue) &&
        !isValidHSLA(inputTextValue) &&
        !isHex('#' + inputTextValue) &&
        !isValidRGBA('rgba(' + inputTextValue + ')') &&
        !isValidHSLA('hsla(' + inputTextValue + ')'))
    ) {
      setInputTextValue(formatColorForInput(colorValue, colorFormat)) // Reset to current valid color if input is empty or invalid
    } else {
      handleColorChange(inputTextValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleColorChange(inputTextValue)
      // @ts-ignore
      e.currentTarget.blur()
    }
  }

  return (
    <div className="a bk">
      <styled.div>
        <SelectInput
          unclearable
          options={[
            { value: 'RGB', label: 'RGBA' },
            { value: 'HSL', label: 'HSL' },
            { value: 'HEX', label: 'HEX' },
          ]}
          onChange={(v: any) => setColorFormat(v)}
          value={colorFormat}
        />
      </styled.div>
      <styled.div className="a7"
        style={{
          '& ::placeholder': {
            color: focused ? colors.neutral20 : colors.neutral80,
          },
        }}
      >
        <TextInput
          autoFocus
          clearable={false}
          value={inputTextValue}
          onChange={(v) => {
            setInputTextValue(v)
          }}
          onKeyDown={handleKeyDown}
          placeholder={formatColorForInput(colorValue, colorFormat)}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
        />
      </styled.div>
      <Stack
        align="center"
        justify="end"
        fitContent
        style={{
          position: 'absolute',
          right: 14,
          bottom: 14,
          height: 24,
        }}
      >
        <IconButton
          size="small"
          icon="copy"
          onClick={() => navigator.clipboard.writeText(colorValue)}
        />
      </Stack>
    </div>
  )
}
